import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { SUPPORTED, LANG_LABELS } from '../i18n/translations';
import { useFavicon } from '../utils/useFavicon';

// #1107 — LANG_LABELS now imported from the canonical i18n source (covers
// all 7 SUPPORTED languages). The previous local 5-entry map was missing
// fr + pt, so those buttons rendered with blank labels.

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: #FAF8F5;
    color: #2C2C2C;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Animations ─────────────────────────────────────── */
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .fadeInUp {
    opacity: 0;
    animation: fadeInUp 0.7s ease-out forwards;
  }
  .delay-1 { animation-delay: 0.15s; }
  .delay-2 { animation-delay: 0.3s; }
  .delay-3 { animation-delay: 0.45s; }
  .delay-card-1 { animation-delay: 0.5s; }
  .delay-card-2 { animation-delay: 0.7s; }
  .delay-card-3 { animation-delay: 0.9s; }

  /* ── Nav ─────────────────────────────────────────────── */
  .nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 40px;
    background: rgba(250, 248, 245, 0.85);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .nav-logo {
    /* #238 PR-2 — text wordmark replaced by full-lockup SVG. Height
       tuned to 40px so the icon block sits comfortably in the 70px-ish
       sticky nav strip, matching the visual weight of the previous
       1.15rem text. */
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    line-height: 0;
  }
  .nav-logo img {
    height: 40px;
    width: auto;
    max-width: 220px;
    display: block;
  }

  .nav-links {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .nav-link {
    font-size: 0.9rem;
    font-weight: 500;
    color: #555;
    text-decoration: none;
    transition: color 0.2s;
  }
  .nav-link:hover { color: #2C2C2C; }

  .nav-cta {
    background: #E8845A;
    color: #fff !important;
    padding: 10px 22px;
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.9rem;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;
  }
  .nav-cta:hover {
    transform: scale(1.03);
    background: #d9724a;
    box-shadow: 0 6px 20px rgba(232, 132, 90, 0.4);
  }

  /* ── Hero ────────────────────────────────────────────── */
  .hero {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 100px 40px 80px;
    background: radial-gradient(ellipse 80% 60% at 50% 40%, #FFF0E8 0%, #FAF8F5 60%, #FAF8F5 100%);
    position: relative;
    overflow: hidden;
  }

  .hero-inner {
    max-width: 1100px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: center;
  }

  .hero-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .hero-photo {
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(0,0,0,0.14);
    aspect-ratio: 4/5;
    position: relative;
  }
  .hero-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .hero-photo::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 55%, rgba(232,132,90,0.10) 100%);
    pointer-events: none;
  }

  /* Decorative soft orb */
  .hero::before {
    content: '';
    position: absolute;
    top: -120px; right: -100px;
    width: 500px; height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(232,132,90,0.10) 0%, transparent 70%);
    pointer-events: none;
  }

  .nonprofit-banner {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: rgba(232,132,90,0.08);
    border: 1px solid rgba(232,132,90,0.2);
    border-radius: 50px;
    padding: 8px 18px;
    margin-bottom: 28px;
    font-size: 0.85rem;
    color: #555;
  }
  .nonprofit-badge {
    background: #E8845A;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 3px 10px;
    border-radius: 20px;
  }
  .trial-badge {
    display: inline-block;
    margin-top: 16px;
    font-size: 0.85rem;
    color: #888;
    letter-spacing: 0.01em;
  }
  .trial-badge strong {
    color: #E8845A;
    font-weight: 600;
  }

  .hero-eyebrow {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #E8845A;
    margin-bottom: 28px;
  }

  .hero-headline {
    font-size: clamp(2.2rem, 4.5vw, 3.6rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.03em;
    color: #1A1A1A;
    max-width: 560px;
    margin-bottom: 24px;
  }

  .hero-subline {
    font-size: clamp(1rem, 1.8vw, 1.15rem);
    font-weight: 400;
    line-height: 1.65;
    color: #555;
    max-width: 460px;
    margin-bottom: 44px;
  }

  @media (max-width: 960px) {
    .hero { padding: 100px 24px 60px; }
    .hero-inner { grid-template-columns: 1fr; gap: 40px; }
    .hero-text { align-items: center; text-align: center; }
    .hero-photo { max-width: 380px; margin: 0 auto; aspect-ratio: 4/3; }
  }

  .btn-primary {
    display: inline-block;
    background: #E8845A;
    color: #fff;
    font-family: inherit;
    font-size: 1.05rem;
    font-weight: 600;
    padding: 16px 36px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    text-decoration: none;
    min-height: 56px;
    line-height: 1.2;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;
  }
  .btn-primary:hover {
    transform: scale(1.03);
    background: #d9724a;
    box-shadow: 0 8px 28px rgba(232, 132, 90, 0.45);
  }

  /* ── #741 Self-wedge ───────────────────────────────────── */
  .self-wedge {
    padding: 56px 24px;
    background: #FFF8F0;
    border-top: 1px solid #F0E0D0;
    border-bottom: 1px solid #F0E0D0;
    text-align: center;
  }
  .self-wedge-inner {
    max-width: 720px;
    margin: 0 auto;
  }
  .self-wedge-headline {
    font-size: 1.6rem;
    font-weight: 700;
    color: #1A1A1A;
    margin: 0 0 12px;
    letter-spacing: -0.01em;
  }
  .self-wedge-subline {
    font-size: 1rem;
    color: #5A4E42;
    margin: 0 0 24px;
    line-height: 1.5;
  }
  .btn-primary-outline {
    display: inline-block;
    padding: 14px 28px;
    background: transparent;
    color: #E8845A;
    font-size: 1rem;
    font-weight: 600;
    text-decoration: none;
    border: 2px solid #E8845A;
    border-radius: 999px;
    transition: background 0.2s, color 0.2s, transform 0.2s;
  }
  .btn-primary-outline:hover {
    background: #E8845A;
    color: #fff;
    transform: scale(1.03);
  }

  /* ── Values ──────────────────────────────────────────── */
  .values {
    padding: 100px 24px;
    background: #FAF8F5;
  }

  .values-inner {
    max-width: 1040px;
    margin: 0 auto;
  }

  .values-label {
    text-align: center;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #E8845A;
    margin-bottom: 56px;
  }

  .values-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
  }

  .value-card {
    background: #FFFFFF;
    border-radius: 16px;
    padding: 40px 32px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    opacity: 0;
  }
  .value-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.10);
  }

  .value-icon {
    font-size: 2.2rem;
    margin-bottom: 20px;
    display: block;
  }

  .value-heading {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1A1A1A;
    margin-bottom: 12px;
    letter-spacing: -0.01em;
  }

  .value-description {
    font-size: 0.95rem;
    line-height: 1.65;
    color: #666;
  }

  /* ── Footer ──────────────────────────────────────────── */
  .footer {
    background: #F2EFE9;
    border-top: 1px solid rgba(0,0,0,0.06);
    padding: 40px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .footer-signin {
    font-size: 0.9rem;
    color: #666;
  }

  .footer-signin a {
    color: #E8845A;
    font-weight: 600;
    text-decoration: none;
    margin-left: 6px;
    transition: color 0.2s;
  }
  .footer-signin a:hover { color: #d9724a; }

  .lang-switcher {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }
  .lang-switcher-nav {
    margin-right: 8px;
  }
  .lang-switcher-nav .lang-btn {
    font-size: 0.75rem;
    padding: 4px 10px;
  }

  .lang-btn {
    background: none;
    border: 1px solid rgba(0,0,0,0.12);
    border-radius: 20px;
    padding: 5px 14px;
    font-family: inherit;
    font-size: 0.8rem;
    color: #666;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s, background 0.2s;
  }
  .lang-btn:hover {
    border-color: #E8845A;
    color: #E8845A;
  }
  .lang-btn.active {
    background: #E8845A;
    border-color: #E8845A;
    color: #fff;
    font-weight: 600;
  }

  .footer-copy {
    font-size: 0.78rem;
    color: #aaa;
  }

  /* ── Responsive ──────────────────────────────────────── */
  @media (max-width: 640px) {
    .nav { padding: 16px 20px; }
    .nav-links { gap: 14px; }
    .hero { padding: 100px 20px 60px; }
    .values { padding: 70px 20px; }
  }
`;

export default function Landing() {
  const { lang, setLang, t } = useLang();
  // Public surface — teal favicon. See utils/useFavicon for the
  // per-surface palette + rationale.
  useFavicon('public');

  return (
    <>
      <style>{styles}</style>

      {/* ── Nav ── */}
      <nav className="nav">
        <a href="/" className="nav-logo" aria-label="Amigo Mio home">
          <img src="/logo/logo-full-watercolor.png" alt="Amigo Mio — AI Voice Companion" />
        </a>
        <div className="nav-links">
          <div className="lang-switcher lang-switcher-nav">
            {SUPPORTED.map(l => (
              <button
                key={l}
                className={`lang-btn${lang === l ? ' active' : ''}`}
                onClick={() => setLang(l)}
              >
                {LANG_LABELS[l]}
              </button>
            ))}
          </div>
          <Link to="/login" className="nav-link">{t.nav.signIn}</Link>
          <Link to="/signup" className="nav-link nav-cta">{t.nav.createProfile}</Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-text">
            <div className="nonprofit-banner fadeInUp">
              <span className="nonprofit-badge">{t.nonprofit.badge}</span>
              <span>{t.nonprofit.text}</span>
            </div>
            <h1 className="hero-headline fadeInUp delay-1">{t.hero.headline}</h1>
            <p className="hero-subline fadeInUp delay-2">{t.hero.subline}</p>
            <Link to="/signup" className="btn-primary fadeInUp delay-3">{t.hero.cta}</Link>
            <span className="trial-badge fadeInUp delay-3"><strong>✓</strong> {t.nonprofit.trial}</span>
          </div>
          <div className="hero-photo fadeInUp delay-2">
            <img
              src="/hero.jpg"
              alt="Elderly woman smiling warmly"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ── #741 — Self-perspective wedge ────────────────────────────
          One-section nudge below the hero for elderly users setting up
          Amigo Mio for themselves. Same product, different angle: the
          intake just renders in second person. Routes to /signup with
          ?for=self so the signup choice screen pre-selects "For
          myself". */}
      <section className="self-wedge">
        <div className="self-wedge-inner">
          <h2 className="self-wedge-headline">{t.selfWedge?.headline || 'Setting it up for yourself?'}</h2>
          <p className="self-wedge-subline">{t.selfWedge?.subline || 'Yes — that works too. Create your own account; the questions just change tone to match.'}</p>
          <Link to="/signup?for=self" className="btn-primary-outline">
            {t.selfWedge?.cta || 'Create my own account'}
          </Link>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="values">
        <div className="values-inner">
          <p className="values-label">Why families choose Amigo Mio</p>
          <div className="values-grid">
            {t.values.map((v, i) => (
              <div
                key={i}
                className={`value-card fadeInUp delay-card-${i + 1}`}
              >
                <span className="value-icon">{v.icon}</span>
                <h3 className="value-heading">{v.heading}</h3>
                <p className="value-description">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <p className="footer-signin">
          {t.footer.alreadyHaveAccount}
          <Link to="/login">{t.footer.signIn}</Link>
        </p>
        <div className="lang-switcher">
          {SUPPORTED.map(l => (
            <button
              key={l}
              className={`lang-btn${lang === l ? ' active' : ''}`}
              onClick={() => setLang(l)}
            >
              {LANG_LABELS[l]}
            </button>
          ))}
        </div>
        <p className="footer-copy">© {new Date().getFullYear()} Amigo Mio</p>
      </footer>
    </>
  );
}
